/**
 * FR-143 · Kích hoạt tài khoản từ lời mời, và xác minh đổi email (FR-120).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, ensureCsrfCookie, useLoiApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import type { InvitationInfo, MeResponse } from '../../lib/types';
import { Field } from '../../components/Field';
import { PasswordStrength } from '../../components/PasswordStrength';
import { Skeleton } from '../../components/States';
import { AuthLayout, FormAlert } from './AuthLayout';

export function InvitePage() {
  const t = useT();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const { setSession } = useAuth();

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  /*
   * OQ-243 — so khớp NGAY khi gõ, không đợi bấm "Kích hoạt và đăng nhập".
   *
   * Suy từ giá trị hiện tại thay vì đọc `mismatch`: state ấy chỉ đổi lúc submit,
   * nên sau một lần báo lệch nó ở lại đỏ trong khi người dùng đã gõ đúng.
   *
   * `confirm.length > 0` — chưa gõ gì thì chưa có gì để sai.
   */
  const lech = confirm.length > 0 && password !== confirm;
  const [busy, setBusy] = useState(false);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await ensureCsrfCookie();
        const data = await api.get<InvitationInfo>(`/auth/invitation/${encodeURIComponent(token)}`);
        if (alive) setInfo(data);
      } catch (err) {
        if (alive && err instanceof ApiError) setLoadError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    setBusy(true);
    setError(null);
    try {
      const me = await api.post<MeResponse>(
        `/auth/invitation/${encodeURIComponent(token)}/accept`,
        { fullName, password },
      );
      setSession(me);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AuthLayout title={t('auth.invite.title')}>
        <div className="stack">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </div>
      </AuthLayout>
    );
  }

  if (loadError || !info) {
    return (
      <AuthLayout
        title={t('auth.invite.title')}
        foot={
          <Link className="small" to="/login">
            {t('auth.2fa.backToLogin')}
          </Link>
        }
      >
        <FormAlert>{loadError?.message ?? t('state.notFound.body')}</FormAlert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('auth.invite.title')}
      lede={t('auth.invite.lede', { inviter: info.invitedBy, role: info.roleName })}
      foot={
        <Link className="small" to="/login">
          {t('auth.2fa.backToLogin')}
        </Link>
      }
    >
      <form
        className="stack"
        onChange={xoaLoiKhiGo.onChange}
        onSubmit={submit}
        data-od-id="form-invite"
      >
        {error ? <FormAlert>{error.message}</FormAlert> : null}

        <div className="field">
          <label htmlFor="invite-email">{t('auth.login.email')}</label>
          <input id="invite-email" type="email" value={info.email} readOnly disabled />
        </div>

        <Field
          label={t('auth.invite.fullName')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          autoFocus
          required
          minLength={2}
          error={error?.fieldMessage('fullName')}
        />

        <Field
          label={t('auth.reset.newPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          error={error?.fieldMessage('newPassword') ?? error?.fieldMessage('password')}
          trailing={<PasswordStrength value={password} />}
        />

        <Field
          label={t('auth.reset.confirmPassword')}
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          required
          error={lech || mismatch ? t('auth.mismatch') : undefined}
        />

        <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
          {busy ? t('common.processing') : t('auth.invite.submit')}
        </button>
      </form>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'busy' | 'ok' | 'error'>('busy');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        await ensureCsrfCookie();
        const data = await api.post<{ email: string }>('/auth/email/verify', { token });
        if (!alive) return;
        setEmail(data.email);
        setState('ok');
      } catch (err) {
        if (!alive) return;
        setMessage(err instanceof ApiError ? err.message : t('state.error.body'));
        setState('error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [token, t]);

  return (
    <AuthLayout
      title={t('auth.verifyEmail.title')}
      foot={
        <Link className="small" to="/profile">
          {t('nav.profile')}
        </Link>
      }
    >
      {state === 'busy' ? <Skeleton height={40} /> : null}
      {state === 'ok' ? (
        <FormAlert tone="ok">{t('auth.verifyEmail.done', { email })}</FormAlert>
      ) : null}
      {state === 'error' ? <FormAlert>{message}</FormAlert> : null}
    </AuthLayout>
  );
}
